import { type CityArea, useV1CityAreasSuspense } from "@hermes/api";
import { useLocale } from "@hermes/intl";
import { searchParamTypes as t, useSearchParams } from "@hermes/shared";
import { createContext, type ReactNode, useContext, useEffect } from "react";

import { useCity } from ".";

const CityAreaContext = createContext<{
  isFetched?: boolean;
  cityAreas?: CityArea[];
  cityArea?: CityArea;
  cityAreaSlug?: string;
  changeCityAreBySlug: (slug?: string) => void;
} | null>(null);

/**
 * City area provider, related only to city_area query parameter
 */
export const CityAreaProvider = ({ children }: { children: ReactNode }) => {
  const [locale] = useLocale();
  const { city } = useCity();
  const [params, setParams] = useSearchParams(
    {
      city_area: t.string<string>({}),
    },
    { allowUnknown: true },
  );

  const { data, isFetched } = useV1CityAreasSuspense({
    city: city.id,
    enabled: Boolean(params.city_area),
    locale: locale.baseName,
  });
  const cityAreas = data !== null ? data.results : undefined;
  const cityArea =
    params.city_area && cityAreas
      ? cityAreas.find((area) => area.slug === params.city_area)
      : undefined;

  useEffect(() => {
    if (params.city_area && params.city_area !== cityArea?.slug) {
      setParams({ city_area: undefined });
    }
  }, [cityArea?.slug, params.city_area]);

  return (
    <CityAreaContext.Provider
      value={{
        isFetched,
        cityAreas,
        cityArea,
        cityAreaSlug: params.city_area,
        changeCityAreBySlug: (slug?: string) => {
          setParams({ city_area: slug });
        },
      }}
    >
      {children}
    </CityAreaContext.Provider>
  );
};

export const useCityArea = () => {
  const value = useContext(CityAreaContext);

  if (!value) {
    throw new Error("useCityArea requires CityAreaProvider");
  }

  return value;
};
